<template>
  <v-card class="pa-4">
    <v-dialog v-model="supplierDialog" max-width="900px">
      <ManageSupplier
        title="Novo fornecedor"
        @close="
          getSuppliers(), (dialogKey = !dialogKey), (supplierDialog = false)
        "
        :key="dialogKey"
      />
    </v-dialog>

    <h3 class="text-center my-2">
      {{ isEditing ? $t('Atualizar entrada') : $t('Nova entrada') }}
    </h3>

    <v-form ref="form">
      <v-row class="ma-0" dense>
        <v-col cols="12" sm="5">
          <span class="text ml-1">{{ $t('Fornecedor') }}</span>
          <v-autocomplete
            v-model="entry.supplierId"
            :items="suppliers"
            item-text="name"
            item-value="id"
            :rules="[(v) => !!v || 'Fornecedor obrigatório']"
            append-icon="mdi-chevron-down"
            class="primary-input"
            :placeholder="$t('Selecione o fornecedor')"
            hide-details="auto"
            outlined
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="d-flex align-center" cols="1" sm="1" md="1" lg="1">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                color="#BBCF20"
                height="30"
                class="mt-1"
                dark
                v-bind="attrs"
                v-on="on"
                x-small
                @click="supplierDialog = true"
              >
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Novo fornecedor') }}</span>
          </v-tooltip>
        </v-col>

        <v-col cols="12" sm="6">
          <span class="text ml-1">{{ $t('Data da entrada') }}</span>
          <div class="d-flex">
            <date-picker
              ref="datepicker"
              style="width: 100%"
              v-model="entry.entryDate"
              mode="dateTime"
              is24hr
              :popover="{ visibility: 'click' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  :class="
                    requestDateTimeError ? 'date-input-error' : 'date-input'
                  "
                  :value="inputValue"
                  v-on="inputEvents"
                  v-mask="['##/##/#### ##:##']"
                />
                <span class="date-span-error" v-if="requestDateTimeError">
                  {{ $t('Data da entrada é requerida') }}
                </span>
              </template>
            </date-picker>
          </div>
        </v-col>

        <v-col cols="12" sm="4">
          <span class="text ml-1">{{ $t('Quantidade') }}</span>
          <v-text-field
            v-if="itemUnitIsUnidade"
            :disabled="isEditing"
            v-model="entry.quantity"
            :rules="[
              (v) => (!!v && parseFloat(v) > 0) || 'Quantidade obrigatória',
            ]"
            hide-details="auto"
            type="number"
            class="primary-input"
            placeholder="0000"
            outlined
            @keypress="preventDot"
            @paste="roundOnPaste"
          ></v-text-field>

          <v-currency-field
            v-else
            :disabled="isEditing"
            v-model="entry.quantity"
            :rules="[(v) => !!v || 'Quantidade obrigatória']"
            :decimal-length="3"
            :options="{
              length: 4,
              precision: 3,
              empty: 0.0,
              masked: false,
            }"
            :max-value="9999"
            :min-value="0"
            class="primary-input"
            hide-details="auto"
            placeholder="0.000"
            outlined
            @input="limitValue"
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-currency-field>
        </v-col>

        <v-col cols="12" sm="4">
          <span class="text ml-1">{{ $t('Valor unitário') }}</span>
          <v-currency-field
            v-model="entry.unitaryValue"
            class="primary-input"
            placeholder="0000"
            validate-on-blur
            hide-details="auto"
            outlined
          >
          </v-currency-field>
        </v-col>

        <v-col cols="12" sm="4">
          <span class="text ml-1">{{ $t('Valor total') }}</span>
          <v-currency-field
            :value="getTotalValue"
            readonly
            class="primary-input"
            placeholder="0000"
            validate-on-blur
            hide-details="auto"
            outlined
          >
          </v-currency-field>
        </v-col>

        <v-col cols="12" sm="6">
          <span class="text ml-1">{{ $t('Nota Fiscal') }}</span>
          <v-text-field
            v-model="entry.nf"
            class="primary-input"
            :placeholder="$t('Insira o número da NF')"
            :rules="[(v) => !!v || 'Nota fiscal obrigatória']"
            outlined
            hide-details="auto"
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="6" v-if="!isEditing">
          <span class="text ml-1">{{ $t('Arquivos') }}</span>
          <v-file-input
            @change="setFiles"
            multiple
            prepend-icon=""
            class="primary-input"
            :placeholder="$t('Selecione arquivos')"
            hide-details="auto"
            outlined
          >
            <template v-slot:selection="{ index, text }">
              <v-chip
                v-if="index < 2"
                color="deep-purple accent-4"
                dark
                label
                small
              >
                {{ text }}
              </v-chip>

              <span
                v-else-if="index === 2"
                class="text-overline grey--text text--darken-3 mx-2"
              >
                +{{ entry.files.length - 2 }} File(s)
              </span>
            </template>
          </v-file-input>
        </v-col>
      </v-row>
    </v-form>

    <div class="d-flex justify-end">
      <v-btn class="secondary-button" depressed @click="$emit('close')">
        {{ $t('Cancelar') }}
      </v-btn>
      <v-btn
        :loading="loading"
        class="primary-button"
        depressed
        @click="submit"
      >
        {{ isEditing ? $t('Atualizar') : $t('Cadastrar') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ApiService from '../../../../services/ApiService'
import FormatDate from '../../../../Utils/FormatDate'
import ManageSupplier from '../../Suppliers/components/ManageSupplier.vue'

import moment from 'moment'

export default {
  name: 'ManageStockEntry',

  props: {
    entryProp: Object,
    stockItemId: Number,
    stockItemUnit: String,
    institutionId: Number,
  },

  data() {
    return {
      requestDateTimeError: false,
      dialogKey: false,
      supplierDialog: false,
      loading: false,
      entry: {
        entryDate: moment().format('YYYY-MM-DD HH:mm '),
        files: [],
      },
      suppliers: [],
      supLoading: false,
      apiService: new ApiService(),
      isEditing: false,
      user: {},
    }
  },

  components: {
    ManageSupplier,
  },

  computed: {
    itemUnitIsUnidade() {
      if (!this.stockItemUnit) return true

      if (this.stockItemUnit == 'Unidade') return true

      return false
    },

    getTotalValue() {
      return this.entry.quantity != null
        ? this.entry.quantity * this.entry.unitaryValue
        : 0
    },
  },

  mounted() {
    this.getSuppliers()
    this.user = JSON.parse(localStorage.getItem('user'))

    this.entry.stockItemId = this.stockItemId
    this.entry.registrationUserId = this.user.id

    if (this.entryProp) {
      this.isEditing = true
      this.entry = { ...this.entryProp }
      this.entry.quantity = this.entryProp.enteredQuantity
      this.entry.entryDate = this.entry.entryDate
        ? this.entry.entryDate.split('T')[0]
        : ''
    }
  },

  methods: {
    preventDot(event) {
      if (event.key === '.' || event.key === ',') {
        event.preventDefault()
      }
    },
    limitCurrencyField() {
      if (this.entry.quantity && parseFloat(this.entry.quantity) > 9999) {
        this.entry.quantity = 9999
      }
    },
    limitValue(value) {
      if (value && parseFloat(value) > 9999) {
        this.$nextTick(() => {
          this.entry.quantity = 9999.0
        })
      }
    },
    roundOnPaste(event) {
      event.preventDefault()
      event.stopPropagation()
      const pasted = (event.clipboardData || window.clipboardData).getData(
        'text'
      )
      const integerPart = pasted.split(/[,.]/)[0].replace(/[^0-9]/g, '')
      if (integerPart) {
        const newValue = parseInt(integerPart)
        this.$set(this.entry, 'quantity', newValue)
      }
    },

    setFiles(event) {
      event.forEach((file) => {
        const reader = new FileReader()

        reader.readAsDataURL(file)

        reader.onload = () => {
          const extension = file.name.split('.')[1]
          this.entry.files.push({
            name: file.name,
            base64: reader.result,
            extension,
          })
        }
      })
    },

    formatDate(date) {
      return FormatDate.dateTime(date)
    },

    getSuppliers() {
      this.supLoading = true

      this.apiService
        .get(`supplier/list/${this.institutionId}`)
        .then((response) => {
          this.suppliers = response.content
          this.supLoading = false
        })
        .catch(() => {
          this.supLoading = false
        })
    },

    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true

        if (this.isEditing) {
          await this.apiService
            .put('stockEntry/edit', this.entry)
            .then(() => {
              this.$toast.success('Entrada de estoque atualizada com sucesso')
              this.loading = false
              this.$emit('confirm')
            })
            .catch(() => {
              this.loading = false
            })
        } else {
          await this.apiService
            .post('stockEntry/add', this.entry)
            .then(() => {
              this.$toast.success('Entrada de estoque cadastrada com sucesso')
              this.loading = false
              this.$emit('confirm')
            })
            .catch(() => {
              this.loading = false
            })
        }
      } else {
        this.requestDateTimeError = this.entry.entryDate == null
      }
    },
  },
  watch: {
    'entry.entryDate': {
      deep: true,
      handler() {
        this.requestDateTimeError = false
      },
    },
  },
}
</script>

<style scoped>
.date-input {
  background: var(--primary-input-background) !important;
  font-family: var(--primary-input-font) !important;
  font-size: var(--primary-input-font-size) !important;
  font-weight: var(--primary-input-font-weigth) !important;
  line-height: var(--primary-input-line-height) !important;
  letter-spacing: var(--primary-input-letter-spacing) !important;
  border-radius: var(--primary-input-border-radius) !important;
  height: 45px;
  width: 100%;
  border: 1px solid #707070 !important;
  padding-left: 10px !important;
}

.date-input-error {
  background: var(--primary-input-background) !important;
  font-family: var(--primary-input-font) !important;
  font-size: var(--primary-input-font-size) !important;
  font-weight: var(--primary-input-font-weigth) !important;
  line-height: var(--primary-input-line-height) !important;
  letter-spacing: var(--primary-input-letter-spacing) !important;
  border-radius: var(--primary-input-border-radius) !important;
  height: 45px;
  width: 100%;
  border: 2px solid #ff5252 !important;
  padding-top: 0 !important;
}
</style>
